import { useRef, useState } from "react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { GoogleCard } from "@/components/settings/GoogleCard";
import { MicrosoftCard } from "@/components/settings/MicrosoftCard";
import { InboxCard } from "@/components/settings/InboxCard";
import { WorkspaceCard } from "@/components/settings/WorkspaceCard";
import { ChannelList } from "@/components/settings/ChannelList";
import {
  useDisconnectLinkedIn,
  useImportConnections,
  useImportLegacy,
  useLinkedIn,
  useSyncLinkedIn,
} from "../lib/queries";
import { parseLegacyExport, type LegacyCounts, type LegacyExport } from "../lib/legacy-import";
import { parseConnectionsCsv } from "../lib/connections-csv";
import { signOut } from "../lib/auth";

export const Route = createFileRoute("/settings/connections")({
  component: Settings,
  // The LinkedIn callback is a redirect, not a fetch, so its result arrives
  // as a search param. `.catch` keeps a hand-edited URL from throwing.
  validateSearch: z.object({
    linkedin: z.string().optional().catch(undefined),
  }),
});

/**
 * Connections — the plumbing, and whether each piece is still working.
 *
 * Was `/settings` itself until Settings became a menu. It kept the whole body
 * and lost only the title: a list of six cards was the entire settings screen,
 * which meant billing, the path and the account had nowhere to go.
 *
 * Ordered by how often something needs attention rather than by importance.
 * Connections lead because they are the only things here that *break* — an
 * address gets rotated, a LinkedIn consent expires at about a year with no
 * refresh token, a mail filter gets deleted — and every one of those failures is
 * silent. The workspace facts below never break; they are here to be read.
 */
function Settings() {
  return (
    <div className="space-y-6 pb-16">
      <header className="space-y-2">
        <p className="text-2xs tracking-[0.14em] text-muted-foreground/70 uppercase">
          Settings
        </p>
        <h1 className="font-display text-4xl leading-[1.05] tracking-tight">
          Connections
        </h1>
        <p className="max-w-2xl text-base leading-relaxed text-muted-foreground">
          What Omina is plugged into, and whether each one is still working.
        </p>
      </header>

      {/* Every channel at a glance, including the two there is no route into.
          See ChannelList for why they are listed rather than hidden. */}
      <ChannelList />

      <GoogleCard />
      <MicrosoftCard />
      <InboxCard />
      <ConnectionsCard />
      <ImportCard />
      <WorkspaceCard />
      <SessionCard />
    </div>
  );
}

/**
 * Your LinkedIn connections.
 *
 * Rule #5 has not moved: nothing here scrapes LinkedIn or automates a
 * session. There are exactly two legitimate ways to get this list, and both
 * are here — the portability API that EU law obliged LinkedIn to publish, and
 * the archive export every member can download. Which one is available is not
 * a preference: the API only works for members whose LinkedIn profile sits in
 * the EEA or Switzerland, and the CSV is what everyone else has.
 */
function ConnectionsCard() {
  const linkedin = useLinkedIn();
  const sync = useSyncLinkedIn();
  const disconnect = useDisconnectLinkedIn();
  const importer = useImportConnections();

  const [progress, setProgress] = useState<string | null>(null);
  const [preview, setPreview] = useState<ConnectionsPreview | null>(null);
  const [csv, setCsv] = useState<CsvStage>({ kind: "idle" });
  const csvRef = useRef<HTMLInputElement>(null);

  // The callback comes back to /settings?linkedin=<status>, because an OAuth
  // redirect cannot land on a fetch.
  const { linkedin: callbackStatus } = Route.useSearch();

  const state = linkedin.data;
  const connected = state?.connected === true;

  /**
   * Walk the export.
   *
   * The loop lives here rather than on the server because Workers allow 50
   * subrequests per invocation — a founder with three thousand connections
   * cannot be one request. The visible side effect is the better one: she
   * sees a number climbing instead of a spinner that might be dead.
   */
  async function runSync(confirm: boolean) {
    setPreview(null);
    let start: number | undefined = undefined;
    let fresh = 0;
    let known = 0;
    let seen = 0;
    const sample: string[] = [];
    const unknownKeys = new Set<string>();

    for (;;) {
      const page = await sync.mutateAsync({ confirm, start });
      fresh += page.counts.fresh;
      known += page.counts.known;
      seen += page.counts.seen;
      for (const name of page.freshSample) if (sample.length < 8) sample.push(name);
      for (const key of page.unknownKeys) unknownKeys.add(key);
      setProgress(`${seen} read…`);

      if (page.nextStart === null) break;
      start = page.nextStart;
    }

    setProgress(null);
    setPreview({ confirmed: confirm, fresh, known, seen, sample, unknownKeys: [...unknownKeys] });
  }

  async function pickCsv(file: File | undefined) {
    importer.reset();
    setCsv({ kind: "idle" });
    if (!file) return;
    const parsed = parseConnectionsCsv(await file.text(), file.name);
    if (!parsed.ok) return setCsv({ kind: "error", issues: parsed.issues });
    setCsv({ kind: "file", name: file.name, rows: parsed.data, found: parsed.result.rows.length });
  }

  const csvResult = importer.data;

  return (
    <Card>
      <CardHeader className="pb-3">
        <Label asChild>
          <span>LinkedIn</span>
        </Label>
      </CardHeader>

      <CardContent className="space-y-3 pt-0">
        {callbackStatus === "connected" && (
          <Alert variant="success">
            <b>Connected.</b> Sync to pull your connections in.
          </Alert>
        )}
        {callbackStatus && callbackStatus !== "connected" && (
          <Alert variant="danger">
            {CALLBACK_MESSAGE[callbackStatus] ?? "LinkedIn refused the connection."}
          </Alert>
        )}

        {connected ? (
          <>
            <p className="hint">
              Connected {new Date(state.grantedAt!).toLocaleDateString()}
              {state.lastSyncAt
                ? ` · last synced ${new Date(state.lastSyncAt).toLocaleDateString()}`
                : " · never synced"}
            </p>

            {state.expiringSoon && (
              // LinkedIn issues no refresh token for this API. Consent simply
              // stops after about a year, so she has to see the wall coming
              // rather than meet it during a sync.
              <Alert variant="danger">
                Your LinkedIn consent expires in {state.daysLeft} days. LinkedIn does not
                renew it silently — you will need to reconnect.
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button disabled={sync.isPending} onClick={() => void runSync(false)}>
                {progress ?? (sync.isPending ? "Reading…" : "Check what's new")}
              </Button>
              <Button
                variant="outline"
                disabled={sync.isPending || disconnect.isPending}
                onClick={() => disconnect.mutate({ purge: false })}
              >
                Disconnect
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="hint">
              Omina reads your connections through LinkedIn's own data-portability API —
              with your consent, and never by scraping. It is available to members whose
              LinkedIn profile is in the EU, EEA or Switzerland. Everywhere else, use the
              file below.
            </p>

            {/*
              Always rendered, exactly like the Google button on /login.
              An earlier version hid it whenever the Worker had no LinkedIn
              keys, which meant the feature simply did not appear to exist —
              a missing button reads as "Omina cannot do this", when the truth
              is "this deployment has not been given the keys yet". A disabled
              button that says why is honest; an absent one is not.

              A link rather than a fetch when it works: an OAuth redirect has
              to be a real navigation or the consent screen has nowhere to
              land. `<a>` cannot be disabled, so the unavailable case is a
              real <button>.
            */}
            {state?.configured === false ? (
              <>
                <Button variant="outline" size="lg" className="w-full" disabled>
                  <LinkedInMark />
                  Connect LinkedIn
                </Button>
                <p className="hint">
                  Not available on this deployment yet — it needs LinkedIn API keys on the
                  server. The file below works today, and works everywhere.
                </p>
              </>
            ) : (
              <Button asChild variant="outline" size="lg" className="w-full">
                <a href="/api/linkedin/authorize">
                  <LinkedInMark />
                  Connect LinkedIn
                </a>
              </Button>
            )}
          </>
        )}

        {preview && (
          <>
            <Alert variant={preview.confirmed ? "success" : "muted"}>
              {preview.confirmed ? (
                <>
                  <b>Synced.</b> {preview.fresh} new · {preview.known} already here.
                </>
              ) : (
                <>
                  Read {preview.seen}. <b>{preview.fresh} new</b>, {preview.known} already in
                  your circle. Nothing written yet.
                </>
              )}
            </Alert>
            {preview.sample.length > 0 && (
              <p className="hint">New: {preview.sample.join(", ")}…</p>
            )}
            {preview.unknownKeys.length > 0 && (
              // If LinkedIn renames a column this is the difference between
              // "0 connections" being a mystery and being a one-line fix.
              <p className="hint">
                Columns we did not recognise: {preview.unknownKeys.join(", ")}
              </p>
            )}
            {!preview.confirmed && preview.fresh + preview.known > 0 && (
              <Button disabled={sync.isPending} onClick={() => void runSync(true)}>
                Add {preview.fresh} to my circle
              </Button>
            )}
          </>
        )}

        {sync.isError && <Alert variant="danger">{syncMessage(sync.error.message)}</Alert>}
      </CardContent>

      <CardContent className="space-y-3 border-t border-border/60 pt-5">
        <Label asChild>
          <span>…or import the file LinkedIn gives you</span>
        </Label>
        <p className="hint">
          LinkedIn: Settings → Data privacy → Get a copy of your data → Connections. It
          takes about ten minutes to arrive by email. Re-upload any time — Omina merges,
          and finds who is new since last time.
        </p>

        <label className="filedrop">
          <input
            ref={csvRef}
            type="file"
            accept="text/csv,.csv"
            onChange={(e) => void pickCsv(e.target.files?.[0])}
          />
          {csv.kind === "file" ? `${csv.name} — ${csv.found} connections` : "Choose Connections.csv"}
        </label>

        {csv.kind === "error" &&
          csv.issues.map((issue) => (
            <Alert variant="danger" key={issue}>
              {issue}
            </Alert>
          ))}

        {csv.kind === "file" && !csvResult?.confirmed && (
          <Button
            disabled={importer.isPending}
            onClick={() => importer.mutate({ connections: csv.rows, confirm: false })}
          >
            Check what would change
          </Button>
        )}

        {csvResult && (
          <>
            <Alert variant={csvResult.confirmed ? "success" : "muted"}>
              {csvResult.confirmed ? (
                <>
                  <b>Imported.</b> {csvResult.counts.fresh} new ·{" "}
                  {csvResult.counts.known} already here.
                </>
              ) : (
                <>
                  <b>{csvResult.counts.fresh} new</b>, {csvResult.counts.known} already in
                  your circle. {csvResult.message}
                </>
              )}
            </Alert>
            {csvResult.possibleDuplicates > 0 && (
              // Matched by name with no LinkedIn URL on either side. Probably
              // the same person; possibly not. Her call, with the number in
              // front of her.
              <p className="hint">
                {csvResult.possibleDuplicates} matched by name only — if you added them by
                hand before, they will be merged rather than duplicated.
              </p>
            )}
            {!csvResult.confirmed && csv.kind === "file" && (
              <Button
                disabled={importer.isPending}
                onClick={() => importer.mutate({ connections: csv.rows, confirm: true })}
              >
                Add {csvResult.counts.fresh} to my circle
              </Button>
            )}
          </>
        )}

        {importer.isError && <Alert variant="danger">{importer.error.message}</Alert>}
      </CardContent>
    </Card>
  );
}

/** LinkedIn's mark, inline — same reasoning as GoogleMark on /login: an
 *  <img> would be a third-party request, and this is a first-party origin. */
function LinkedInMark() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden focusable="false">
      <path
        d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.63-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zm1.78 13.02H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"
      />
    </svg>
  );
}

interface ConnectionsPreview {
  confirmed: boolean;
  fresh: number;
  known: number;
  seen: number;
  sample: string[];
  unknownKeys: string[];
}

type CsvStage =
  | { kind: "idle" }
  | { kind: "file"; name: string; rows: Record<string, string>[]; found: number }
  | { kind: "error"; issues: string[] };

/** What each redirect status means, in her words rather than OAuth's. */
const CALLBACK_MESSAGE: Record<string, string> = {
  denied: "You declined the LinkedIn consent screen — nothing was connected.",
  state: "That sign-in didn't complete safely. Please try connecting again.",
  not_configured: "LinkedIn isn't configured on this deployment yet.",
  expired: "LinkedIn refused the request. Try connecting again.",
  version: "LinkedIn's API rejected our version. This is our bug, not yours.",
  rate_limited: "LinkedIn is rate-limiting us. Try again in a few minutes.",
  unavailable: "LinkedIn didn't respond. Try again in a moment.",
};

function syncMessage(raw: string): string {
  if (raw.includes("linkedin_expired")) {
    return "Your LinkedIn consent has run out. Reconnect to keep syncing.";
  }
  if (raw.includes("linkedin_rate")) return "LinkedIn is rate-limiting us — try again shortly.";
  return raw;
}

type ImportStage =
  | { kind: "idle" }
  | { kind: "file"; name: string; data: LegacyExport; counts: LegacyCounts; exportedAt?: string }
  | { kind: "error"; issues: string[] };

/**
 * Import from the prototype.
 *
 * Two gates on purpose. The file is parsed in the browser first, so a wrong
 * file never leaves the machine and a file carrying secrets is refused before
 * upload. Then the server runs a dry run — it plans every row and writes
 * nothing — and only after she has seen those numbers does the destructive
 * call become available.
 *
 * The destructiveness is the point, not an oversight: the import wipes and
 * replaces this workspace's loop, maps and rooms, which is what makes a failed
 * import safe to retry. Merging would double every contact on the second try.
 */
function ImportCard() {
  const [stage, setStage] = useState<ImportStage>({ kind: "idle" });
  const fileRef = useRef<HTMLInputElement>(null);
  const importer = useImportLegacy();

  function reset() {
    setStage({ kind: "idle" });
    importer.reset();
    if (fileRef.current) fileRef.current.value = "";
  }

  async function pick(file: File | undefined) {
    importer.reset();
    if (!file) return setStage({ kind: "idle" });

    const result = parseLegacyExport(await file.text());
    if (!result.ok) return setStage({ kind: "error", issues: result.issues });

    setStage({
      kind: "file",
      name: file.name,
      data: result.data,
      counts: result.counts,
      ...(result.exportedAt ? { exportedAt: result.exportedAt } : {}),
    });
  }

  const plan = importer.data;
  const dryRun = plan && !plan.confirmed ? plan : null;
  const done = plan && plan.confirmed ? plan : null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="eyebrow">Import</div>
        <p className="why">
          The JSON from <code>scripts/export-legacy.js</code> in the old app.
          Importing <b>replaces</b> this workspace&rsquo;s people, follow-ups,
          outcomes, rooms and maps — it never merges.
        </p>
      </CardHeader>

      <CardContent className="space-y-3.5 pt-0">
        <label className="filedrop">
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            onChange={(e) => void pick(e.target.files?.[0])}
          />
          {stage.kind === "file" ? stage.name : "Choose omina-export-….json"}
        </label>

        {stage.kind === "error" && (
          <Alert variant="danger">
            {stage.issues.map((issue) => (
              <div key={issue}>{issue}</div>
            ))}
          </Alert>
        )}

        {stage.kind === "file" && !done && (
          <>
            <p className="why">
              In the file
              {stage.exportedAt ? ` (exported ${stage.exportedAt.slice(0, 10)})` : ""}:
            </p>
            <Counts counts={stage.counts} />

            {!dryRun && (
              <Button
                disabled={importer.isPending}
                onClick={() => importer.mutate({ export: stage.data, confirm: false })}
              >
                Check what would change
              </Button>
            )}
          </>
        )}

        {dryRun && (
          <>
            <Alert variant="danger">
              <b>This replaces everything in this workspace.</b> After confirming,
              what is here now is gone and the file&rsquo;s version is what
              remains.
            </Alert>
            <p className="why">What would be written:</p>
            <Counts counts={dryRun.counts} />
            <Orphans moves={dryRun.orphanedMoves} outcomes={dryRun.orphanedOutcomes} />
            <div className="flex flex-wrap items-center gap-2">
              {/* `destructive` rather than the solid coral: this is the one
                  irreversible button in the product, and it should not look
                  like every other confirm. It is a quiet outline that turns
                  oxblood on approach. */}
              <Button
                variant="destructive"
                disabled={importer.isPending}
                onClick={() =>
                  stage.kind === "file" &&
                  importer.mutate({ export: stage.data, confirm: true })
                }
              >
                Yes — replace my workspace
              </Button>
              <Button variant="ghost" onClick={reset}>
                Cancel
              </Button>
            </div>
          </>
        )}

        {done && (
          <>
            <Alert variant="success">
              <b>Imported.</b> Your loop is here.
            </Alert>
            <Counts counts={done.counts} />
            <Orphans moves={done.orphanedMoves} outcomes={done.orphanedOutcomes} />
            <Button variant="outline" onClick={reset}>
              Done
            </Button>
          </>
        )}

        {importer.isError && <Alert variant="danger">{importer.error.message}</Alert>}
      </CardContent>
    </Card>
  );
}

function Counts({ counts }: { counts: LegacyCounts }) {
  const rows: Array<[string, number]> = [
    ["People", counts.contacts],
    ["Follow-ups", counts.moves],
    ["Outcomes", counts.outcomes],
    ["Rooms", counts.rooms],
    ["Maps", counts.maps],
    ["Profile", counts.profile],
  ];
  return (
    <div className="counts">
      {rows.map(([label, n]) => (
        <div key={label}>
          <b>{n}</b>
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Rows the import cannot keep.
 *
 * The prototype wrote the literal string "manual" as a move id when a contact
 * had no move yet, so real exports carry outcomes pointing at nothing. They
 * cannot be stored — `outcomes.move_id` is a real foreign key now — and they
 * teach the brain nothing either way. Saying so is the difference between "I
 * imported everything" being true and being roughly true.
 */
function Orphans({ moves, outcomes }: { moves: number; outcomes: number }) {
  if (!moves && !outcomes) return null;
  return (
    <p className="why" style={{ marginTop: 12 }}>
      {outcomes > 0 && (
        <>
          {outcomes} outcome{outcomes === 1 ? "" : "s"} point at a follow-up that
          no longer exists and {outcomes === 1 ? "is" : "are"} skipped — the
          contact state they produced still imports.{" "}
        </>
      )}
      {moves > 0 && (
        <>
          {moves} follow-up{moves === 1 ? "" : "s"} reference a missing contact
          and {moves === 1 ? "is" : "are"} skipped.
        </>
      )}
    </p>
  );
}

function SessionCard() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  /** Sign out and land on /login with nothing of hers left in memory. */
  async function leave() {
    // Drop every cached answer before navigating. Invalidating would only
    // mark them stale — the next person at this browser would still see the
    // last one's loop for the frame before the refetch lands.
    queryClient.clear();
    await router.navigate({ to: "/login", replace: true });
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="eyebrow">Session</div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <Button
          variant="outline"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            await signOut();
            await leave();
          }}
        >
          Sign out
        </Button>

        <p className="hint">
          Signs you out of this browser. Your workspace, your profile and everything in
          your loop stay exactly where they are — sign back in with Google and it is all
          here.
        </p>
      </CardContent>
    </Card>
  );
}
